/**
 * RBAC roles, personas, permissions, and module-access maps for AstraConnect.
 *
 * Design decisions (see plan docs/superpowers/plans/2026-06-17-role-persona-rbac.md):
 * - Persona is the PRIMARY access gate. Permissions are SECONDARY refinements
 *   within a persona's already-granted surface.
 * - PERSONA_MODULES is an INTERIM compile-time seed (Phase 1). It does NOT satisfy
 *   AC-ADM-3 ("changing a persona definition requires no code deploy"). AC-ADM-3 is
 *   satisfied only by the Phase 2 server-driven `myPersonaDefinition` endpoint.
 *   Do not mark AC-ADM-3 done until CurrentUserProvider consumes that endpoint.
 * - Role + persona code VALUES below are CONFIRMED against the connect-gql source
 *   of truth (AppRoles + member_360 seed, migration 001): role codes lowercase
 *   ("admin"), persona codes uppercase ("PROVIDER"…). The care-mgmt #35173 proxy
 *   returns these verbatim. Remaining TBD: PERMISSIONS value strings (below).
 */

// `import type` only — erased at compile time, so this introduces no runtime
// circular dependency with featureCatalog.ts (which imports PERSONA_MODULES from
// here). Typing the seed values as ModuleId[] makes catalog/seed drift a tsc error.
import type { ModuleId } from "./featureCatalog";

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * Known role code strings returned by `connectCurrentUser.role.code` (AcRole).
 * CONFIRMED against connect-gql: AppRoles (`src/domain/constants/AppRoles.ts`) +
 * the `member_360.roles` seed (migration 001) → role codes are LOWERCASE
 * ("admin"/"clinical"/"readonly"); RequireAppRole compares `r.code === "admin"`.
 */
export const ROLES = {
  ADMIN: "admin", // connect-gql AppRoles.ADMIN = "admin" (lowercase); roles seed = ('admin','Admin')
} as const;

export type RoleValue = (typeof ROLES)[keyof typeof ROLES];

/**
 * The full set of role codes the backend accepts (lowercase), CONFIRMED against
 * connect-gql AppRoles + the `member_360.roles` seed (migration 001):
 * admin / clinical / readonly. `ROLES` above only promotes ADMIN to a named
 * constant (it's the only one the FE gates on), but bulk-import client-side
 * validation must accept all three so legitimate clinical/readonly rows are not
 * rejected before submit. Extend this set if the backend roles seed grows.
 */
export const KNOWN_ROLE_CODES: ReadonlySet<string> = new Set([
  "admin",
  "clinical",
  "readonly",
]);

// ---------------------------------------------------------------------------
// Personas — PRD-authoritative (AstraConnect PRD §4, 2026-06-18)
// ---------------------------------------------------------------------------

/**
 * The AstraConnect personas per PRD §4.
 * CONFIRMED against connect-gql `member_360.personas` seed (migration 001:70-72) —
 * codes are UPPERCASE and match these constants verbatim, so `connectCurrentUser`
 * persona codes pass through BACKEND_PERSONA_MAP 1:1 (identity map below).
 *
 * NOTE: the "ADMIN" persona is DELIBERATELY ABSENT here. AstraConnect admin is
 * governed solely by the `admin` ROLE (see ROLES.ADMIN + the role-keyed all-access
 * in canSeeModule / useFeatureAccess). The old ADMIN super-persona was a duplicate
 * admin signal; it is now vestigial — the member_360.personas seed row may still
 * exist in the DB, but the FE neither offers it nor grants access from it. Do NOT
 * re-add "ADMIN" here: it would resurrect the second, persona-based admin gate.
 */
export const PERSONAS = {
  PROVIDER: "PROVIDER",                     // member_360.personas seed
  CUSTOMER_SERVICE: "CUSTOMER_SERVICE",     // member_360.personas seed
  MSO: "MSO",                               // member_360.personas seed
  CARE_MANAGER: "CARE_MANAGER",             // member_360.personas seed
  MEDICAL_ASSISTANT: "MEDICAL_ASSISTANT",   // member_360.personas seed
} as const;

export type PersonaValue = (typeof PERSONAS)[keyof typeof PERSONAS];

/**
 * Persona codes the FE must NOT surface in any persona picker/filter, even if the
 * backend `listPersonas` still returns them. "ADMIN" lives here: the member_360
 * seed row may persist, but the ADMIN persona is vestigial — admin access is the
 * ROLE's job. Filtering it out of option lists stops admins from being told to
 * assign a persona that no longer grants anything. Case-insensitive compare.
 */
export const HIDDEN_PERSONA_CODES: ReadonlySet<string> = new Set(["ADMIN"]);

/** True if `code` is a persona the FE should hide from pickers/filters. */
export function isHiddenPersona(code: string | null | undefined): boolean {
  return !!code && HIDDEN_PERSONA_CODES.has(code.toUpperCase());
}

// ---------------------------------------------------------------------------
// ADAPTER — Backend persona string → FE PERSONAS enum key
// ---------------------------------------------------------------------------
/**
 * Maps the raw code string returned by `connectCurrentUser.currentPersona.code` /
 * `personas[].code` to the canonical FE PersonaValue.
 *
 * CONFIRMED: persona codes from connect-gql member_360.personas seed (migration 001)
 * are UPPERCASE and match the PERSONAS constants verbatim, so this is an identity map.
 * If the backend ever changes its code strings, update the LEFT-hand side (keys) here
 * — the right-hand PERSONAS values are the stable FE identifiers.
 *
 * SECURITY NOTE: normalizePersona() returns undefined for any code NOT present here.
 * CurrentUserContext treats an unrecognized code as fail-closed (keeps the raw string
 * so canSeeModule denies all modules) — NOT as the allow-all null seam. Do not add
 * a catch-all entry or default that maps unknown codes to a known persona.
 */
export const BACKEND_PERSONA_MAP: Record<string, PersonaValue> = {
  [PERSONAS.PROVIDER]:           PERSONAS.PROVIDER,
  [PERSONAS.CUSTOMER_SERVICE]:   PERSONAS.CUSTOMER_SERVICE,
  [PERSONAS.MSO]:                PERSONAS.MSO,
  [PERSONAS.CARE_MANAGER]:       PERSONAS.CARE_MANAGER,
  [PERSONAS.MEDICAL_ASSISTANT]:  PERSONAS.MEDICAL_ASSISTANT,
  // "ADMIN" intentionally omitted — normalizePersona("ADMIN") now returns
  // undefined, so a stale backend ADMIN-persona code fails closed (canSeeModule
  // denies) rather than unlocking everything. Admin access comes from the ROLE.
};

/**
 * Normalize a backend persona string to the canonical FE PersonaValue.
 * Returns undefined if the string is unrecognized.
 */
export function normalizePersona(backendValue: string | null | undefined): PersonaValue | undefined {
  if (!backendValue) return undefined;
  return BACKEND_PERSONA_MAP[backendValue];
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

/**
 * Known `value` strings from `connectCurrentUser.permissions[].value` (AcPermission).
 * These are secondary gates within a persona's already-allowed surface.
 * TODO: confirm exact permission value strings with connect-gql schema (care-mgmt #35173).
 */
export const PERMISSIONS = {
  VIEW_CLAIMS:           "view_claims",           // TODO: confirm backend string
  VIEW_MEDICATIONS:      "view_medications",      // TODO: confirm backend string
  ADMIN_USER_MANAGEMENT: "admin_user_management", // TODO: confirm backend string
  // Extend as additional permission.value keys are confirmed.
} as const;

export type PermissionValue = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ---------------------------------------------------------------------------
// PERSONA_MODULES — INTERIM hardcoded map (Phase 1 seed only)
// ---------------------------------------------------------------------------
/**
 * Maps each persona to the moduleId strings it can access.
 * Source: AstraConnect PRD §4 (2026-06-18, unchanged as of 2026-06-23).
 *
 * ⚠️  INTERIM / PHASE 1 SEED — this map is a compile-time fallback only.
 * AC-ADM-3 ("persona→tab mapping is admin-editable without a code deploy")
 * is satisfied ONLY by the Phase 2 `myPersonaDefinition` GQL endpoint.
 * CurrentUserProvider will prefer the server definition once it lands and
 * demote this constant to a fallback. Do NOT mark AC-ADM-3 complete until
 * that endpoint is live on all envs.
 *
 * Surfaces annotated "new upstream dependency" do not yet exist in
 * @astrana/ui — they are included here so the data model is complete (Phase 1),
 * but the tabs will not render until upstream modules land (see plan §9).
 */
export const PERSONA_MODULES: Record<string, readonly ModuleId[]> = {
  [PERSONAS.PROVIDER]: [
    "Home",           // summary, quality gaps, HCC
    "Medications",
    "AmbientAI",      // new upstream dependency — see plan §9
    "WriteBack",      // new upstream dependency — see plan §9
    "Auths",
  ],
  [PERSONAS.CUSTOMER_SERVICE]: [
    "MemberSearch",
    "Eligibility",
    "Claims",
    "CareTeam",
    "Outreach",       // new upstream dependency — see plan §9
  ],
  [PERSONAS.MSO]: [
    "NetworkSearch",  // new upstream dependency — see plan §9
    "Home",           // quality gaps + HCC subset
    "Claims",
    "Auths",
    "Outreach",       // new upstream dependency — see plan §9
  ],
  [PERSONAS.CARE_MANAGER]: [
    // CM-8319 — Next Best Action card + its Activity history tab. Seeded to
    // CARE_MANAGER only: the NBA playbooks are care-management workflows (TOC
    // follow-up, AWV, gap closure) addressed to a care team. As with every id
    // here this seed is a compile-time vocabulary anchor, NOT a runtime grant —
    // real enablement is a member_360.persona_modules row, so widening to other
    // personas is a DB change (or an admin-console edit), not a code change.
    "HomeNextBestAction",
    "Activity",
    "Home",           // quality gaps subset
    "CareTeam",
    "Eligibility",
    "Auths",
    "Outreach",       // new upstream dependency — see plan §9
  ],
  [PERSONAS.MEDICAL_ASSISTANT]: [
    "Eligibility",
    "Documents",
    "PracticeSchedule", // admin-fed; new upstream dependency — see plan §9
    "Home",             // quality gaps subset
  ],
} as const;

// ---------------------------------------------------------------------------
// UNGATED_MODULES — existing tabs that are NOT in the PRD persona vocabulary
// ---------------------------------------------------------------------------
/**
 * Tabs that exist in ALL_MODULES today but are absent from the PRD persona table
 * (Diagnoses, Labs, and any future additions in the same category). These are
 * always shown regardless of the resolved persona, preserving pre-RBAC behaviour.
 *
 * INTERIM — Phase 2 (AC-ADM-3): once persona definitions are server-driven these
 * tabs should be added to the server-side definitions instead of this list.
 *
 * Rule: a tab's moduleId (after tabNameToModuleId normalization) belongs here if
 * it (a) is currently visible to users, AND (b) does not appear in ANY persona's
 * PERSONA_MODULES entry above.
 */
export const UNGATED_MODULES: ReadonlySet<string> = new Set([
  // "Diagnoses" was here until sql/0033_hide_diagnoses_tab.sql (2026-09-01):
  // the tab is now hidden for EVERY persona by deactivating its server-side
  // grant, and member-360 has had no client-side UNGATED bypass since AC-ADM-3
  // (m360 #377) — this set is a test-only mirror. It must not claim Diagnoses
  // is "always visible" while the data says the opposite.
  "Labs",      // not in PRD persona vocabulary — always visible (interim)
]);

// ---------------------------------------------------------------------------
// MODULE_PERMISSIONS — secondary permission gates within a persona's surface
// ---------------------------------------------------------------------------
/**
 * Maps moduleId → the permission.value required to access it (on top of persona gate).
 * Absent/null = no secondary permission gate; persona membership alone is sufficient.
 * TODO (§8 Q3): confirm which modules require a secondary permission and exact values.
 *
 * Phase 2 (AC-ADM-3): secondary permission gating deferred — values unconfirmed by
 * Pathways. MODULE_PERMISSIONS is exported for Phase 2 wiring but canSeeModule does
 * NOT consult it yet; enforcing unconfirmed strings would risk a new regression.
 */
export const MODULE_PERMISSIONS: Record<string, string | null> = {
  Claims:      PERMISSIONS.VIEW_CLAIMS,
  Medications: PERMISSIONS.VIEW_MEDICATIONS,
  // null/absent = visible to any user whose persona includes the module
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if `persona` grants access to `moduleId` per the INTERIM
 * PERSONA_MODULES map. This is the PHASE 1 implementation of the module gate.
 *
 * Phase 2: CurrentUserProvider will call the server-driven `myPersonaDefinition`
 * endpoint and `canSeeModule` will use that definition instead. The call-site
 * API is stable — consumers call `useCurrentUser().canSeeModule(moduleId)`.
 *
 * `opts.ignorePersonaCheck` — set true in tests that only care about secondary
 * permission logic; skips the persona gate.
 * `opts.role` — the caller's role code. The `admin` role is an all-access signal:
 * an admin sees EVERY module regardless of persona (admins manage the system).
 * This replaces the old ADMIN super-persona so admin-ness has a SINGLE source of
 * truth (the role), not a duplicate persona flag.
 */
export function canSeeModule(
  persona: string | null | undefined,
  moduleId: string,
  opts: { ignorePersonaCheck?: boolean; role?: string | null } = {},
): boolean {
  // Role-driven all-access: the admin role grants every module regardless of
  // persona. Single admin signal — see ROLES.ADMIN and the PERSONAS note.
  if (opts.role === ROLES.ADMIN) return true;
  if (!opts.ignorePersonaCheck) {
    if (!persona) return false;
    const allowed = PERSONA_MODULES[persona];
    if (!allowed) return false;
    if (!(allowed as readonly string[]).includes(moduleId)) return false;
  }
  return true;
}

/**
 * Returns true if `permissionValues` (a Set of permission.value strings) contains
 * `value`. Used as the secondary gate inside a persona's allowed surface.
 */
export function hasPermission(
  permissionValues: Set<string>,
  value: string,
): boolean {
  return permissionValues.has(value);
}
